import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import * as echarts from "echarts";
import { useNavigate } from "react-router-dom";
import { FilterBar, SearchRow } from "../MarketTrad/shared";
import NlimsDataTable from "../shared/NlimsDataTable/NlimsDataTable";
import { useCountUp } from "../../hooks/useCountUp";
import { useAuth } from "../../context/AuthContext";
import {
  workplansApi,
  workplanActivitiesApi,
  activityReportsApi,
  archiveDeletedWorkplan,
  archiveDeletedActivityReport,
  formatCurrencyCompact,
  normalizeDateValue,
  parseMoney,
  sumNumericValues,
  WORKPLAN_PLAN_TYPES,
  WORKPLAN_STATUSES,
  WORKPLAN_GEO_SCOPES,
  ACTIVITY_REPORT_STATUSES,
} from "../../api/planningApi";
import { supplementaryDatasetApi } from "../../api/supplementaryDatasetApi";
import { CreateNewModal } from "./PARCreateModal";
import { usePlanningReferenceOptions } from "./usePlanningReferenceOptions";
import { ActionDropdown, anchorRect, QuickConfirmModal, RejectNotesModal, WORKFLOW_RULES, actionCellHtml } from "../shared/RowActionMenu";

function getApiErrorMessage(error) {
  const response = error?.response?.data;
  const fieldErrors = response?.errors ?? response?.data?.errors;
  if (fieldErrors && typeof fieldErrors === "object") {
    const msgs = Object.entries(fieldErrors)
      .flatMap(([, v]) => (Array.isArray(v) ? v : [String(v)]))
      .filter(Boolean)
      .slice(0, 3);
    if (msgs.length) return msgs.join(" • ");
  }
  if (response?.message) return response.message;
  if (error?.message) return error.message;
  return "Something went wrong.";
}

function getRecordId(row) {
  return row?.uuid ?? row?.id ?? row?.raw?.uuid ?? row?.raw?.id ?? null;
}

function normalizeTextSearch(row = {}) {
  return Object.values(row)
    .filter((value) => typeof value === "string")
    .join(" ")
    .toLowerCase();
}

function uniqueList(values = []) {
  return [...new Set(values.map((value) => String(value ?? "").trim()).filter(Boolean))];
}

function usePlanningCollection(api) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.listAll();
      const items = Array.isArray(result?.items) ? result.items : [];
      setRows(items);
      return items;
    } catch (err) {
      setRows([]);
      setError(getApiErrorMessage(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refresh().catch(() => {});
  }, [refresh]);

  const create = useCallback(async (payload) => {
    setError("");
    try {
      const created = await api.create(payload);
      await refresh();
      return created;
    } catch (err) {
      setError(getApiErrorMessage(err));
      throw err;
    }
  }, [api, refresh]);

  const update = useCallback(async (row, payload) => {
    const uuid = getRecordId(row);
    if (!uuid) throw new Error("Missing record identifier.");
    setError("");
    try {
      const updated = await api.update(uuid, payload);
      await refresh();
      return updated;
    } catch (err) {
      setError(getApiErrorMessage(err));
      throw err;
    }
  }, [api, refresh]);

  const remove = useCallback(async (row, archiveFn) => {
    const uuid = getRecordId(row);
    if (!uuid) throw new Error("Missing record identifier.");
    setError("");
    try {
      const response = await api.remove(uuid);
      if (archiveFn) archiveFn(row, response);
      await refresh();
      return response;
    } catch (err) {
      setError(getApiErrorMessage(err));
      throw err;
    }
  }, [api, refresh]);

  const runAction = useCallback(async (row, method, ...args) => {
    const uuid = getRecordId(row);
    if (!uuid) throw new Error("Missing record identifier.");
    setError("");
    try {
      const result = await api[method](uuid, ...args);
      await refresh();
      return result;
    } catch (err) {
      setError(getApiErrorMessage(err));
      throw err;
    }
  }, [api, refresh]);

  return { rows, setRows, loading, error, refresh, create, update, remove, runAction };
}

function usePlanningCreateContext({ workplans = [], activities = [], reports = [] } = {}) {
  const { user } = useAuth();

  const referenceOptions = usePlanningReferenceOptions({
    workplans,
    activities,
    reports,
    currentUserUuid: user?.uuid ?? "",
    currentUserLabel: user?.name ?? "",
    currentInstitutionUuid: user?.institutionUuid ?? "",
    currentInstitutionLabel: user?.institution ?? "",
  });

  return {
    referenceOptions,
    workplans,
    activities,
    currentUserUuid: user?.uuid ?? "",
    currentUserLabel: user?.name ?? "",
    currentInstitutionUuid: user?.institutionUuid ?? "",
    currentInstitutionLabel: user?.institution ?? "",
  };
}

function statusChartData(rows, field, labels) {
  const total = rows.length || 1;
  const colors = ["#374151", "#3b82f6", "#22c55e", "#16a34a", "#eab308", "#ef4444"];
  return labels.map((label, index) => {
    const count = rows.filter((row) => row?.[field] === label).length;
    return {
      label,
      count,
      value: Math.round((count / total) * 100),
      color: colors[index % colors.length],
    };
  });
}

function ByStatusChart({ data }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const chart = echarts.getInstanceByDom(ref.current) || echarts.init(ref.current);
    chart.setOption({
      grid: { top: 4, bottom: 4, left: 68, right: 44, containLabel: false },
      xAxis: { type: "value", max: 100, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { show: false }, splitLine: { show: false } },
      yAxis: { type: "category", data: data.map((item) => item.label), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { fontSize: 10, color: "#6b7280" } },
      series: [
        {
          type: "bar",
          barWidth: 10,
          data: data.map((item) => ({ value: item.value, itemStyle: { color: item.color, borderRadius: 3 } })),
          label: { show: true, position: "insideLeft", fontSize: 9, color: "#fff", formatter: "{c}%" },
        },
        {
          type: "bar",
          barWidth: 10,
          barGap: "-100%",
          data: data.map(() => ({ value: 100, itemStyle: { color: "#f3f4f6" } })),
          silent: true,
          z: 0,
        },
      ],
    });
    chart.resize();
    return () => chart.dispose();
  }, [data]);
  return <div ref={ref} style={{ width: "100%", height: 130 }} />;
}

function WpStatCards({ stats }) {
  const { count: totalCount, ref: totalRef } = useCountUp(stats.totalCount);
  const { count: budgetCount, ref: budgetRef } = useCountUp(Math.round(stats.totalBudget));
  const reviewVal = stats.plansForReview;

  return (
    <div className="mb-6 grid grid-cols-4 gap-4">
      <div ref={totalRef} className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="mb-1 text-xs text-gray-500">Total Workplans</p>
        <p className="text-3xl font-black text-green-600">{totalCount >= 1000 ? `${(totalCount / 1000).toFixed(1)}k` : totalCount}</p>
        <p className="mt-1 text-[9px] text-gray-400">COUNT all plans in registry</p>
        <p className="text-[8px] text-gray-300">Trend vs. last period</p>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="mb-1 text-xs text-gray-500">By Status</p>
        <ByStatusChart data={stats.statusData} />
      </div>
      <div ref={budgetRef} className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="mb-1 text-xs text-gray-500">Budget Committed (NGN)</p>
        <div className="flex items-center gap-2">
          <p className="text-3xl font-black text-green-600">{formatCurrencyCompact(budgetCount)}</p>
          <span className="text-lg text-green-500">↗</span>
        </div>
        <p className="mt-1 text-[9px] text-gray-400">SUM budget where STATUS=Active</p>
        <p className="text-[8px] text-gray-300">Trend vs. last period</p>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="mb-1 text-xs text-gray-500">Plans Due for Review</p>
        <p className={`text-5xl font-black ${reviewVal > 5 ? "text-red-600" : "text-gray-900"}`}>{reviewVal}</p>
        <p className="mt-1 text-[9px] text-gray-400">COUNT approaching end date without completion</p>
        <p className="text-[8px] text-gray-300">Off Track if &gt;5</p>
      </div>
    </div>
  );
}

function GaugeChart({ pct, color }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const chart = echarts.getInstanceByDom(ref.current) || echarts.init(ref.current);
    chart.setOption({
      series: [
        {
          type: "gauge",
          radius: "95%",
          startAngle: 90,
          endAngle: -270,
          min: 0,
          max: 100,
          pointer: { show: false },
          progress: { show: true, width: 8, itemStyle: { color } },
          axisLine: { lineStyle: { width: 8, color: [[1, "#e5e7eb"]] } },
          splitLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          detail: {
            valueAnimation: true,
            formatter: "{value}%",
            fontSize: 12,
            fontWeight: "bold",
            color: "#374151",
            offsetCenter: [0, "0%"],
          },
          data: [{ value: pct }],
        },
      ],
    });
    chart.resize();
    return () => chart.dispose();
  }, [pct, color]);
  return <div ref={ref} style={{ width: 80, height: 80 }} />;
}

function rptColor(value, thresholds) {
  if (value >= thresholds[0]) return "#16a34a";
  if (value >= thresholds[1]) return "#d97706";
  return "#dc2626";
}

function RptStatCards({ stats }) {
  const { count: submittedCount, ref: submittedRef } = useCountUp(stats.submitted);
  const { count: notStartedCount, ref: notStartedRef } = useCountUp(stats.notStarted);
  const completionColor = rptColor(stats.completionRate, [70, 50]);
  const onTimeColor = rptColor(stats.onTimeRate, [85, 70]);
  const outputColor = rptColor(stats.avgOutputAchievement, [80, 60]);

  return (
    <div className="mb-6 grid grid-cols-5 gap-4">
      <div ref={submittedRef} className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-[11px] leading-tight text-gray-500">Reports Submitted This Period</p>
        <p className="mt-2 text-3xl font-black text-gray-900">{submittedCount}</p>
        <p className="mt-1 text-[9px] text-gray-400">COUNT report submissions</p>
      </div>
      <div className="flex flex-col items-center rounded-xl border border-gray-200 bg-white p-4">
        <p className="mb-1 text-center text-[10px] leading-tight text-gray-500">Completion Rate</p>
        <GaugeChart pct={stats.completionRate} color={completionColor} />
        <p className="mt-1 text-center text-[8px] text-gray-400">Green ≥70% / Amber 50-69%</p>
      </div>
      <div className="flex flex-col items-center rounded-xl border border-gray-200 bg-white p-4">
        <p className="mb-1 text-center text-[10px] leading-tight text-gray-500">On-time Submission Rate</p>
        <GaugeChart pct={stats.onTimeRate} color={onTimeColor} />
        <p className="mt-1 text-center text-[8px] text-gray-400">Green ≥85% / Amber 70-84%</p>
      </div>
      <div className="flex flex-col items-center rounded-xl border border-gray-200 bg-white p-4">
        <p className="mb-1 text-center text-[10px] leading-tight text-gray-500">Avg Output Achievement</p>
        <GaugeChart pct={stats.avgOutputAchievement} color={outputColor} />
        <p className="mt-1 text-center text-[8px] text-gray-400">Green ≥80% / Amber 60-79%</p>
      </div>
      <div ref={notStartedRef} className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-[11px] leading-tight text-gray-500">Activities Not Started</p>
        <p className={`mt-2 text-3xl font-black ${notStartedCount ? "text-red-600" : "text-gray-900"}`}>{notStartedCount}</p>
        <p className="mt-1 text-[9px] text-gray-400">Red if &gt;5% of total</p>
      </div>
    </div>
  );
}

function buildWorkplanStats(rows = []) {
  const totalCount = rows.length;
  const totalBudget = rows
    .filter((row) => String(row?.status ?? "").toLowerCase() === "active")
    .reduce((sum, row) => sum + (row?.budgetNgn ?? parseMoney(row?.budget) ?? 0), 0);
  const statusData = statusChartData(rows, "status", WORKPLAN_STATUSES);
  const plansForReview = rows.filter((row) => {
    const status = String(row?.status ?? "").toLowerCase();
    if (["completed", "cancelled", "suspended"].includes(status)) return false;
    const endDate = normalizeDateValue(row?.raw?.end_date ?? row?.raw?.endDate ?? row?.endDate);
    if (!endDate) return false;
    const daysLeft = Math.ceil((endDate.getTime() - Date.now()) / 86400000);
    return daysLeft >= 0 && daysLeft <= 30;
  }).length;

  return { totalCount, totalBudget, statusData, plansForReview };
}

function buildReportStats(rows = []) {
  const totalCount = rows.length;
  const submitted = totalCount;
  const completionRate = totalCount
    ? Math.round((rows.filter((row) => String(row?.executionStatus ?? "").toLowerCase() === "completed").length / totalCount) * 100)
    : 0;
  const onTimeRate = totalCount
    ? Math.round((rows.filter((row) => {
        const submission = normalizeDateValue(row?.raw?.submission_date ?? row?.submissionDate);
        const dueDate = normalizeDateValue(row?.raw?.actual_end_date ?? row?.raw?.end_date ?? row?.endDate);
        return submission && dueDate && submission.getTime() <= dueDate.getTime();
      }).length / totalCount) * 100)
    : 0;
  const outputsTarget = rows.reduce((sum, row) => sum + sumNumericValues(row?.outputsTargetData ?? row?.raw?.outputs_target ?? row?.outputsTarget), 0);
  const outputsAchieved = rows.reduce((sum, row) => sum + sumNumericValues(row?.outputsAchievedData ?? row?.raw?.outputs_achieved ?? row?.outputsAchieved), 0);
  const avgOutputAchievement = outputsTarget > 0 ? Math.round((outputsAchieved / outputsTarget) * 100) : 0;
  const notStarted = rows.filter((row) => String(row?.executionStatus ?? "").toLowerCase() === "not_started").length;

  return { submitted, completionRate, onTimeRate, avgOutputAchievement, notStarted };
}

// Workplan helpers
const WORKPLAN_COLUMNS = [
  { title: "Title", data: "title", sortable: true },
  { title: "Plan Type", data: "planType", sortable: true },
  { title: "Plan Category", data: "planCategory", sortable: true, defaultContent: "" },
  { title: "Period", data: "period", sortable: true },
  { title: "State", data: "state", sortable: true },
  { title: "LGA", data: "lga", sortable: true },
  { title: "Department", data: "department", sortable: true },
  { title: "Budget (NGN)", data: "budget", sortable: true },
  { title: "Plan Owner", data: "planOwner", sortable: true },
  { title: "Status", data: "status", sortable: true, render: (value, type) => (type === "display" ? `<span class="nlims-status nlims-status--${String(value ?? "").toLowerCase().replace(/\s+/g, "-")}">${value ?? ""}</span>` : value ?? "") },
  { title: "Completion %", data: "completionPercent", sortable: true, defaultContent: "", render: (v, type) => (type === "display" ? (v === null || v === undefined || v === "" ? "—" : `${v}%`) : (v ?? "")) },
  { title: "Data Status", data: "dataStatus", sortable: true, render: (value, type) => (type === "display" ? `<span class="nlims-status nlims-status--${String(value ?? "").toLowerCase().replace(/\s+/g, "-")}">${value ?? ""}</span>` : value ?? "") },
  { title: "Approving Authority Level", data: "approvingAuthority", sortable: true },
  { title: "Geographic Scope", data: "geographicScope", sortable: true },
  { title: "Action", data: null, sortable: false, render: () => actionCellHtml() },
];

const WORKPLAN_FILTERS = [
  { key: "planType", label: "Plan Type", options: WORKPLAN_PLAN_TYPES, defaultLabel: "All" },
  { key: "status", label: "Status", options: WORKPLAN_STATUSES, defaultLabel: "All" },
  { key: "geographicScope", label: "Geographic Scope", options: WORKPLAN_GEO_SCOPES, defaultLabel: "All" },
];

export function Workplans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isR05 = user?.roleId === 'R05';
  const roleId = user?.roleId ?? '';
  const canCreateWorkplans = !isR05 && roleId !== 'R02' && roleId !== 'R06';
  const { rows, error, loading, create, remove, runAction } = usePlanningCollection(workplansApi);
  const { rows: activityRows } = usePlanningCollection(workplanActivitiesApi);
  const createContext = usePlanningCreateContext({ workplans: rows, activities: activityRows });
  const [filters, setFilters] = useState({ planType: "", status: "", geographicScope: "" });
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [quickBusy, setQuickBusy] = useState(false);

  const stats = useMemo(() => buildWorkplanStats(rows), [rows]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchSearch = !q || normalizeTextSearch(row).includes(q);
      const matchType = !filters.planType || row.planType === filters.planType;
      const matchStatus = !filters.status || row.status === filters.status;
      const matchGeo = !filters.geographicScope || row.geographicScope === filters.geographicScope;
      return matchSearch && matchType && matchStatus && matchGeo;
    });
  }, [rows, filters, search]);

  const handleCreate = useCallback(async (payload) => {
    const { activities, ...workplanPayload } = payload;
    const created = await create({
      ...workplanPayload,
      institution_uuid: payload.institution_uuid || payload.institutionUuid || user?.institutionUuid,
      plan_owner_uuid: payload.planOwnerUuid || user?.uuid,
    });

    // Activities are repeatable rows of the same DCIC-11 instrument — create
    // them right after the workplan. A failure here doesn't roll back the
    // workplan (matches the Actor+Qualification partial-failure pattern).
    const workplanUuid = created?.uuid ?? created?.id ?? '';
    const newActivities = (activities ?? []).filter((a) => a.activityTitle || a.category);
    if (workplanUuid && newActivities.length > 0) {
      const failedLabels = [];
      for (const activity of newActivities) {
        try {
          await workplanActivitiesApi.create({
            workplan_uuid: workplanUuid,
            title: activity.activityTitle,
            activity_title: activity.activityTitle,
            category: activity.category,
            budget_ngn: activity.budgetNgn,
            target_beneficiaries: activity.targetBeneficiaries,
            beneficiary_class: activity.beneficiaryClass,
            status: 'Planned',
            activity_status: 'Planned',
          });
        } catch {
          failedLabels.push(activity.activityTitle || 'Activity');
        }
      }
      if (failedLabels.length > 0) {
        return { warning: `Workplan saved, but ${failedLabels.length} activity(ies) could not be saved (${failedLabels.join(', ')}).` };
      }
    }
    return undefined;
  }, [create, user]);

  const handleDelete = useCallback(async (payload) => {
    await remove(payload, archiveDeletedWorkplan);
  }, [remove]);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action == null) setActionMenu({ row, rect });
  }, []);

  const runQuickAction = useCallback(async (row, method) => {
    setQuickBusy(true);
    try { await runAction(row, method); }
    finally { setQuickBusy(false); }
  }, [runAction]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setQuickBusy(true);
    try { await handleDelete(deleteTarget); setDeleteTarget(null); }
    finally { setQuickBusy(false); }
  }, [deleteTarget, handleDelete]);

  const handleRejectConfirm = useCallback(async (notes) => {
    if (!rejectTarget) return;
    setQuickBusy(true);
    try { await runAction(rejectTarget, 'reject', notes); setRejectTarget(null); }
    finally { setQuickBusy(false); }
  }, [rejectTarget, runAction]);

  return (
    <div>
      {!isR05 && <FilterBar filters={WORKPLAN_FILTERS} filterValues={filters} onFilterChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))} onApply={() => {}} />}
      <SearchRow searchValue={search} onSearch={setSearch} onCreate={() => navigate("/planning-reporting/new?tab=workplans")} canCreate={canCreateWorkplans} />
      {loading && <div className="mb-4 text-xs text-gray-400">Loading live records...</div>}
      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {!isR05 && <WpStatCards stats={stats} />}
      <NlimsDataTable
        tableId="workplans-table"
        columns={WORKPLAN_COLUMNS}
        data={filtered}
        serverSide={false}
        hideSearch
        pageLength={10}
        enableRowSelection
        onRowAction={handleRowAction}
      />
      {actionMenu && !quickBusy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.workplans}
          onClose={() => setActionMenu(null)}
          onView={() => {
            const row = actionMenu.row;
            setActionMenu(null);
            navigate(`/planning-reporting/view/${getRecordId(row)}?tab=workplans`, { state: { row } });
          }}
          onEdit={() => {
            const row = actionMenu.row;
            setActionMenu(null);
            navigate(`/planning-reporting/edit/${getRecordId(row)}?tab=workplans`, { state: { row } });
          }}
          onSubmit={() => runQuickAction(actionMenu.row, 'submit')}
          onValidate={() => runQuickAction(actionMenu.row, 'validate')}
          onApprove={() => runQuickAction(actionMenu.row, 'approve')}
          onReject={() => { setRejectTarget(actionMenu.row); setActionMenu(null); }}
          onDelete={() => setDeleteTarget(actionMenu.row)}
        />
      )}
      {rejectTarget && (
        <RejectNotesModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}
      {deleteTarget && (
        <QuickConfirmModal
          title="Delete Workplan"
          message={`Permanently delete "${deleteTarget.title || 'this workplan'}"? This cannot be undone.`}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={quickBusy}
        />
      )}
      {createOpen && (
        <CreateNewModal
          tab="workplans"
          context={createContext}
          onSubmit={handleCreate}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </div>
  );
}

const REPORT_COLUMNS = [
  { title: "Parent Activity", data: "activityId", sortable: true },
  { title: "Activity Type", data: "activityType", sortable: true, defaultContent: "" },
  { title: "State", data: "state", sortable: true },
  { title: "LGA", data: "lga", sortable: true },
  { title: "Reporting Period", data: "reportingPeriod", sortable: true },
  { title: "Start Date", data: "startDate", sortable: true },
  { title: "Execution Status", data: "executionStatus", sortable: true, render: (value, type) => (type === "display" ? `<span class="nlims-status nlims-status--${String(value ?? "").toLowerCase().replace(/\s+/g, "-")}">${value ?? ""}</span>` : value ?? "") },
  { title: "Expenditure (NGN)", data: "expenditure", sortable: true },
  { title: "Outputs Target", data: "outputsTarget", sortable: true },
  { title: "Outputs Achieved", data: "outputsAchieved", sortable: true },
  { title: "Variance Explanation", data: "varianceExpl", sortable: true },
  { title: "Data Status", data: "dataStatus", sortable: true, render: (value, type) => (type === "display" ? `<span class="nlims-status nlims-status--${String(value ?? "").toLowerCase().replace(/\s+/g, "-")}">${value ?? ""}</span>` : value ?? "") },
  { title: "Submission Date", data: "submissionDate", sortable: true },
  { title: "Action", data: null, sortable: false, render: () => actionCellHtml() },
];


export function ActivityReports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleId = user?.roleId ?? '';
  const canCreateReports = roleId !== 'R02' && roleId !== 'R06';
  const { rows, error, loading, create, remove, runAction } = usePlanningCollection(activityReportsApi);
  const { rows: activities } = usePlanningCollection(workplanActivitiesApi);
  const createContext = usePlanningCreateContext({ activities, reports: rows });
  const [filters, setFilters] = useState({ executionStatus: "", reportingPeriod: "" });
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [quickBusy, setQuickBusy] = useState(false);

  const stats = useMemo(() => buildReportStats(rows), [rows]);
  const reportingPeriods = useMemo(() => uniqueList(rows.map((row) => row.reportingPeriod)), [rows]);
  const reportFilters = useMemo(
    () => [
      { key: "executionStatus", label: "Execution Status", options: ACTIVITY_REPORT_STATUSES, defaultLabel: "All" },
      { key: "reportingPeriod", label: "Reporting Period", options: reportingPeriods, defaultLabel: "All" },
    ],
    [reportingPeriods]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchSearch = !q || normalizeTextSearch(row).includes(q);
      const matchExec = !filters.executionStatus || row.executionStatus === filters.executionStatus;
      const matchPeriod = !filters.reportingPeriod || row.reportingPeriod === filters.reportingPeriod;
      return matchSearch && matchExec && matchPeriod;
    });
  }, [rows, filters, search]);

  const handleCreate = useCallback(async (payload) => {
    await create({
      workplan_activity_uuid: payload.workplanActivityUuid || payload.parentActivityId,
      parent_activity_id: payload.parentActivityId || payload.workplanActivityUuid,
      activity_type: payload.activityType,
      ppp_id: payload.pppId,
      reporting_period: payload.reportingPeriod,
      actual_start_date: payload.actualStartDate,
      execution_status: payload.executionStatus,
      actual_expenditure_ngn: payload.actualExpenditureNgn || payload.expenditure,
      outputs_target: payload.outputsTarget,
      outputs_achieved: payload.outputsAchieved,
      submission_date: payload.submissionDate,
      reported_by_uuid: payload.reportedByUuid || user?.uuid,
      variance_explanation: payload.varianceExplanation || payload.varianceExpl,
      attachments: payload.attachments,
      attachments_file_name: payload.attachmentsFileName,
    });
  }, [create, user]);

  const handleDelete = useCallback(async (payload) => {
    await remove(payload, archiveDeletedActivityReport);
  }, [remove]);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action == null) setActionMenu({ row, rect });
  }, []);

  const runQuickAction = useCallback(async (row, method) => {
    setQuickBusy(true);
    try { await runAction(row, method); }
    finally { setQuickBusy(false); }
  }, [runAction]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setQuickBusy(true);
    try { await handleDelete(deleteTarget); setDeleteTarget(null); }
    finally { setQuickBusy(false); }
  }, [deleteTarget, handleDelete]);

  const handleRejectConfirm = useCallback(async (notes) => {
    if (!rejectTarget) return;
    setQuickBusy(true);
    try { await runAction(rejectTarget, 'reject', notes); setRejectTarget(null); }
    finally { setQuickBusy(false); }
  }, [rejectTarget, runAction]);

  return (
    <div>
      <RptStatCards stats={stats} />
      <FilterBar filters={reportFilters} filterValues={filters} onFilterChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))} onApply={() => {}} />
      <SearchRow searchValue={search} onSearch={setSearch} onCreate={() => navigate("/planning-reporting/new?tab=reports")} canCreate={canCreateReports} />
      {loading && <div className="mb-4 text-xs text-gray-400">Loading live records...</div>}
      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      <NlimsDataTable tableId="activity-reports-table" columns={REPORT_COLUMNS} data={filtered} serverSide={false} hideSearch pageLength={10} enableRowSelection onRowAction={handleRowAction} />
      {actionMenu && !quickBusy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.activityReports}
          onClose={() => setActionMenu(null)}
          onView={() => {
            const row = actionMenu.row;
            setActionMenu(null);
            navigate(`/planning-reporting/view/${getRecordId(row)}?tab=reports`, { state: { row } });
          }}
          onEdit={() => {
            const row = actionMenu.row;
            setActionMenu(null);
            navigate(`/planning-reporting/edit/${getRecordId(row)}?tab=reports`, { state: { row } });
          }}
          onSubmit={() => runQuickAction(actionMenu.row, 'submit')}
          onValidate={() => runQuickAction(actionMenu.row, 'validate')}
          onApprove={() => runQuickAction(actionMenu.row, 'approve')}
          onReject={() => { setRejectTarget(actionMenu.row); setActionMenu(null); }}
          onDelete={() => setDeleteTarget(actionMenu.row)}
        />
      )}
      {rejectTarget && (
        <RejectNotesModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}
      {deleteTarget && (
        <QuickConfirmModal
          title="Delete Report"
          message="Permanently delete this activity report? This cannot be undone."
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={quickBusy}
        />
      )}
      {createOpen && (
        <CreateNewModal
          tab="reports"
          context={createContext}
          onSubmit={handleCreate}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </div>
  );
}

// PAR Validation History — live backend timeline derived from workflow fields.
const VAL_SUB_TABS = ["All", "Workplan", "Activity Report", "Publication"];
function TypeTag({ type, selected }) {
  return (
    <span className={`shrink-0 whitespace-nowrap rounded-md border px-2 py-0.5 text-xs ${selected ? "border-white text-white" : "border-gray-300 text-gray-600"}`}>
      {type}
    </span>
  );
}

const VAL_BADGE_COLORS = {
  Completed: { bg: "#dcfce7", color: "#15803d" },
  Approved: { bg: "#dcfce7", color: "#15803d" },
  Active: { bg: "#dcfce7", color: "#15803d" },
  Submitted: { bg: "#dbeafe", color: "#1d4ed8" },
  "Pending approval": { bg: "#fef3c7", color: "#b45309" },
  Draft: { bg: "#f3f4f6", color: "#6b7280" },
  Suspended: { bg: "#fef3c7", color: "#b45309" },
  Rejected: { bg: "#fee2e2", color: "#dc2626" },
  Inactive: { bg: "#fee2e2", color: "#dc2626" },
};

function ValStatusBadge({ status }) {
  const c = VAL_BADGE_COLORS[status] || { bg: "#f3f4f6", color: "#6b7280" };
  const hasGreenDot = status === "Approved" || status === "Active" || status === "Completed";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: c.bg, color: c.color }}>
      {hasGreenDot && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a", display: "inline-block" }} />}
      {status}
    </span>
  );
}

function ValDetailRow({ label, children }) {
  return (
    <div className="flex items-start justify-between border-b border-gray-100 py-4 last:border-0">
      <span className="shrink-0 pr-4 text-sm text-gray-400">{label}</span>
      <div className="max-w-[65%] text-right text-sm text-gray-800">{children}</div>
    </div>
  );
}

const HISTORY_STATUS_MAP = {
  submitted: { previousStatus: "Draft", newStatus: "Submitted" },
  validated: { previousStatus: "Submitted", newStatus: "Validated" },
  approved:  { previousStatus: "Validated", newStatus: "Approved" },
  rejected:  { previousStatus: "Submitted", newStatus: "Rejected" },
};

const HISTORY_TYPE_LABELS = {
  workplan: "Workplan",
  activity_report: "Activity Report",
  publication: "Publication",
};

function getPersonName(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    return String(value.name ?? value.full_name ?? value.fullName ?? value.label ?? value.uuid ?? "").trim();
  }
  return String(value).trim();
}

function formatHistoryStamp(value) {
  const date = normalizeDateValue(value);
  if (!date) return "-";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function buildHistoryEvent({
  row,
  type,
  phase,
  timestampField,
  actor,
  comments,
    previousStatus,
    newStatus,
  }) {
  const timestamp = row?.[timestampField];
  if (!timestamp) return null;

  const title =
    row?.title ??
    row?.activityId ??
    row?.dataset_name ??
    row?.workplan_activity?.title ??
    row?.uuid ??
    "Record";

  const eventType = HISTORY_TYPE_LABELS[type] ?? type;
  const actorName = getPersonName(actor) || "System";
  const normalizedDate = formatHistoryStamp(timestamp);

  return {
    id: `${row?.uuid ?? title}:${phase}`,
    title,
    type: eventType,
    previousStatus,
    newStatus,
    actionBy: actorName,
    validatedBy: actorName,
    comments: comments || "-",
    date: normalizedDate,
    fullDate: normalizedDate,
    recordRef: row?.uuid ?? row?.id ?? "-",
    timestamp: new Date(timestamp).getTime(),
  };
}

function buildValidationHistoryRows(type, rows = []) {
  return rows.flatMap((row) => {
    const createdBy = getPersonName(row?.created_by);
    const submittedBy = getPersonName(row?.submitted_by) || createdBy;
    const validatedBy = getPersonName(row?.validated_by);
    const approvedBy = getPersonName(row?.approved_by);
    const rejectionNotes = String(row?.rejection_notes ?? "").trim();

    const recordType = HISTORY_TYPE_LABELS[type] ?? type;
    const baseTitle =
      row?.title ??
      row?.activityId ??
      row?.dataset_name ??
      row?.workplan_activity?.title ??
      row?.uuid ??
      recordType;

    const commentsPrefix = `${recordType} - ${baseTitle}`;
    const events = [
      buildHistoryEvent({
        row,
        type,
        phase: "submitted",
        timestampField: "submitted_at",
        actor: submittedBy || createdBy,
        previousStatus: HISTORY_STATUS_MAP.submitted.previousStatus,
        newStatus: HISTORY_STATUS_MAP.submitted.newStatus,
        comments: `Submitted by ${submittedBy || createdBy || "the originator"}`,
      }),
      buildHistoryEvent({
        row,
        type,
        phase: "validated",
        timestampField: "validated_at",
        actor: validatedBy || submittedBy || createdBy,
        previousStatus: HISTORY_STATUS_MAP.validated.previousStatus,
        newStatus: HISTORY_STATUS_MAP.validated.newStatus,
        comments: `Validated by ${validatedBy || submittedBy || "the reviewer"}`,
      }),
      buildHistoryEvent({
        row,
        type,
        phase: "approved",
        timestampField: "approved_at",
        actor: approvedBy || validatedBy || submittedBy || createdBy,
        previousStatus: HISTORY_STATUS_MAP.approved.previousStatus,
        newStatus: HISTORY_STATUS_MAP.approved.newStatus,
        comments: `Approved by ${approvedBy || validatedBy || "the approver"}`,
      }),
    ].filter(Boolean);

    if (row?.rejected_at) {
      events.push(
        buildHistoryEvent({
          row,
          type,
          phase: "rejected",
          timestampField: "rejected_at",
          actor: validatedBy || approvedBy || submittedBy || createdBy,
          previousStatus: row?.validated_at ? "Validated" : "Submitted",
          newStatus: HISTORY_STATUS_MAP.rejected.newStatus,
          comments: rejectionNotes || `${commentsPrefix} was rejected.`,
        })
      );
    }

    return events.filter(Boolean);
  });
}

export function PARValidationHistory() {
  const [ownData, setOwnData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [subTab, setSubTab] = useState("All");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let active = true;

    const loadHistory = async () => {
      setLoading(true);
      setError("");

      const sources = [
        {
          type: "workplan",
          label: "Workplans",
          fetch: () => workplansApi.listAll({ per_page: 100 }),
        },
        {
          type: "activity_report",
          label: "Activity Reports",
          fetch: () => activityReportsApi.listAll({ per_page: 100 }),
        },
        {
          type: "publication",
          label: "Publications",
          fetch: () => supplementaryDatasetApi.listAll({ per_page: 100 }),
        },
      ];

      const results = await Promise.allSettled(sources.map((source) => source.fetch()));

      if (!active) return;

      const nextRows = [];
      const errors = [];

      results.forEach((result, index) => {
        const source = sources[index];
        if (result.status === "fulfilled") {
          const items = Array.isArray(result.value?.items) ? result.value.items : [];
          nextRows.push(...buildValidationHistoryRows(source.type, items));
        } else {
          errors.push(`${source.label}: ${getApiErrorMessage(result.reason)}`);
        }
      });

      nextRows.sort((a, b) => (b.timestamp - a.timestamp) || String(a.title).localeCompare(String(b.title)));
      setOwnData(nextRows);
      setError(errors.length > 0 ? `Some live history sources failed to load. ${errors.join(" • ")}` : "");
      setLoading(false);
    };

    loadHistory().catch((err) => {
      if (!active) return;
      setError(getApiErrorMessage(err, "Failed to load validation history."));
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  const listData = useMemo(
    () =>
      ownData.filter((row) => {
        if (subTab !== "All" && row.type !== subTab) return false;
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return (
          row.title.toLowerCase().includes(q) ||
          row.type.toLowerCase().includes(q) ||
          row.actionBy.toLowerCase().includes(q) ||
          row.comments.toLowerCase().includes(q)
        );
      }),
    [ownData, subTab, search]
  );

  useEffect(() => {
    setSelected(listData[0] ?? null);
  }, [listData]);

  return (
    <div>
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {VAL_SUB_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setSubTab(tab)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                subTab === tab
                  ? "border border-green-300 bg-green-100 text-green-700"
                  : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:max-w-2xl">
          <label className="flex flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 focus-within:border-green-500">
            <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <circle cx={11} cy={11} r={8} />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search history"
              className="flex-1 bg-transparent text-sm text-gray-600 outline-none placeholder-gray-400"
            />
          </label>
          <span className="inline-flex w-fit items-center rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs font-semibold text-green-700">
            Live backend feed
          </span>
        </div>
      </div>

      {loading && (
        <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700">
          Loading live validation history...
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4 lg:flex-row" style={{ minHeight: 500 }}>
        <div className="flex max-h-[620px] w-full shrink-0 flex-col gap-2 overflow-auto lg:w-80">
          {listData.map((record) => (
            <button
              key={record.id}
              onClick={() => setSelected(record)}
              className={`flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3.5 text-left transition-colors ${
                selected?.id === record.id
                  ? "bg-green-700 text-white"
                  : "border border-gray-200 bg-white hover:bg-gray-50"
              }`}
            >
              <div className="min-w-0">
                <p className={`truncate text-sm font-semibold ${selected?.id === record.id ? "text-white" : "text-gray-900"}`}>
                  {record.title}
                </p>
                <p className={`mt-0.5 text-xs ${selected?.id === record.id ? "text-green-100" : "text-gray-400"}`}>
                  {record.date}
                </p>
              </div>
              <TypeTag type={record.type} selected={selected?.id === record.id} />
            </button>
          ))}
        </div>

        {!selected ? (
          <div className="flex flex-1 items-center justify-center rounded-xl border border-gray-200 text-sm text-gray-400">
            {loading ? "Loading records..." : "Select a record to view details"}
          </div>
        ) : (
          <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <p className="text-sm font-bold text-gray-900">{selected.title}</p>
              <TypeTag type={selected.type} />
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-2">
              <ValDetailRow label="Previous Status:"><ValStatusBadge status={selected.previousStatus} /></ValDetailRow>
              <ValDetailRow label="New Status:"><ValStatusBadge status={selected.newStatus} /></ValDetailRow>
              <ValDetailRow label="Action By:">{selected.actionBy}</ValDetailRow>
              <ValDetailRow label="Validated By:">{selected.validatedBy}</ValDetailRow>
              <ValDetailRow label="Comments:">{selected.comments}</ValDetailRow>
              <ValDetailRow label="Date:">{selected.fullDate}</ValDetailRow>
              <ValDetailRow label="Record Reference:">
                <span className="font-mono text-sm text-gray-700">{selected.recordRef}</span>
              </ValDetailRow>
            </div>
            <div className="flex flex-col gap-2 border-t border-gray-100 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs font-medium text-gray-400">
                Read-only live history from backend workplans, reports, and publication records.
              </p>
              <button
                type="button"
                onClick={() => setSelected(listData[0] ?? null)}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                disabled={listData.length === 0}
              >
                Latest record
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



